import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { zhCN } from "date-fns/locale";
import { ChevronLeft, ChevronRight, History, Loader2 } from "lucide-react";
import { AdminAlert, AdminEmptyState, AdminList, AdminListRow, AdminPill } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";
import {
  fetchActivityEvents,
  fetchLoginEvents,
  type ActivityEvent,
  type LoginEvent,
  type MeListMeta,
} from "@/lib/me-api";
type LogSubTab = "login" | "activity";
type ActivitySource = "main_app" | "other_app";

const PER_PAGE = 20;

const LOGIN_METHOD_LABELS: Record<string, string> = {
  email_otp: "邮箱验证码",
  magic_link: "登录链接",
  wechat_mock: "微信（开发）",
};

function formatEventTime(iso: string) {
  return format(new Date(iso), "yyyy年M月d日 HH:mm", { locale: zhCN });
}

function loginSummary(event: LoginEvent) {
  const method = LOGIN_METHOD_LABELS[event.method] ?? event.method;
  const result = event.result === "success" ? "成功" : "失败";
  return `${method} · ${result}`;
}

function LogsPagination({
  meta,
  page,
  loading,
  onPageChange,
}: {
  meta: MeListMeta | null;
  page: number;
  loading: boolean;
  onPageChange: (page: number) => void;
}) {
  const total = meta?.total ?? 0;
  const perPage = meta?.per_page ?? PER_PAGE;
  const totalPages = total > 0 ? Math.ceil(total / perPage) : 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(page * perPage, total);

  if (total === 0) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3 text-sm text-muted-foreground">
      <span className="tabular-nums">
        {loading ? "加载中…" : `第 ${rangeStart}–${rangeEnd} 条，共 ${total} 条`}
      </span>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading || page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="上一页"
        >
          <ChevronLeft className="size-4" />
          上一页
        </Button>
        <span className="tabular-nums">
          {page} / {totalPages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading || page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label="下一页"
        >
          下一页
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

export function SecurityLogsPanel() {
  const [subTab, setSubTab] = useState<LogSubTab>("login");
  const [activitySource, setActivitySource] = useState<ActivitySource>("main_app");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loginEvents, setLoginEvents] = useState<LoginEvent[]>([]);
  const [activityEvents, setActivityEvents] = useState<ActivityEvent[]>([]);
  const [meta, setMeta] = useState<MeListMeta | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (subTab === "login") {
        const result = await fetchLoginEvents({ page, per_page: PER_PAGE });
        setLoginEvents(result.login_events);
        setMeta(result.meta);
      } else {
        const result = await fetchActivityEvents({ page, per_page: PER_PAGE, source: activitySource });
        setActivityEvents(result.activity_events);
        setMeta(result.meta);
      }
    } catch (caughtError) {
      setError(caughtError instanceof ApiError ? caughtError.message : "加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [subTab, page, activitySource]);

  useEffect(() => {
    void load();
  }, [load]);

  function handleSubTabChange(tab: LogSubTab) {
    setSubTab(tab);
    setPage(1);
  }

  function handleSourceChange(source: ActivitySource) {
    setActivitySource(source);
    setPage(1);
  }

  const showOtherAppPlaceholder = subTab === "activity" && activitySource === "other_app";

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-base font-medium">账号日志</h2>
        <p className="text-sm text-muted-foreground">查看最近登录与关键操作，便于自查与发现异常。</p>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="日志类型">
        {(
          [
            { id: "login" as const, label: "登录记录" },
            { id: "activity" as const, label: "操作记录" },
          ] as const
        ).map(({ id, label }) => (
          <Button
            key={id}
            type="button"
            size="sm"
            variant={subTab === id ? "default" : "outline"}
            aria-pressed={subTab === id}
            onClick={() => handleSubTabChange(id)}
          >
            {label}
          </Button>
        ))}
      </div>

      {subTab === "activity" ? (
        <div className="flex flex-wrap gap-2" role="group" aria-label="操作来源">
          {(
            [
              { id: "main_app" as const, label: "商家中心" },
              { id: "other_app" as const, label: "其他应用" },
            ] as const
          ).map(({ id, label }) => (
            <Button
              key={id}
              type="button"
              size="sm"
              variant={activitySource === id ? "default" : "outline"}
              aria-pressed={activitySource === id}
              onClick={() => handleSourceChange(id)}
            >
              {label}
            </Button>
          ))}
        </div>
      ) : null}

      {error ? <AdminAlert>{error}</AdminAlert> : null}

      {loading && loginEvents.length === 0 && activityEvents.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          加载中…
        </div>
      ) : null}

      {!loading && showOtherAppPlaceholder ? (
        <AdminEmptyState>
          <History className="mx-auto mb-3 size-8 text-muted-foreground/70" />
          <p className="font-medium text-foreground">其他应用的操作记录即将接入</p>
          <p className="mt-2 text-sm">子产品内的关键操作将在此汇总展示。</p>
        </AdminEmptyState>
      ) : null}

      {!showOtherAppPlaceholder && subTab === "login" && !loading && loginEvents.length === 0 ? (
        <AdminEmptyState>
          <History className="mx-auto mb-3 size-8 text-muted-foreground/70" />
          <p className="font-medium text-foreground">暂无登录记录</p>
          <p className="mt-2 text-sm">成功或失败的登录尝试都会出现在这里。</p>
        </AdminEmptyState>
      ) : null}

      {!showOtherAppPlaceholder && subTab === "activity" && !loading && activityEvents.length === 0 ? (
        <AdminEmptyState>
          <History className="mx-auto mb-3 size-8 text-muted-foreground/70" />
          <p className="font-medium text-foreground">暂无操作记录</p>
          <p className="mt-2 text-sm">改邮箱、生成登录链接等操作会留痕在此。</p>
        </AdminEmptyState>
      ) : null}

      {!showOtherAppPlaceholder && subTab === "login" && loginEvents.length > 0 ? (
        <AdminList>
          {loginEvents.map((event) => (
            <AdminListRow key={event.id} className="items-start">
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-medium text-foreground">{loginSummary(event)}</p>
                <p className="text-xs text-muted-foreground tabular-nums">{formatEventTime(event.occurred_at)}</p>
                {event.ip ? (
                  <p className="text-xs text-muted-foreground">
                    {event.location ? `${event.location} · ` : ""}
                    IP {event.ip}
                  </p>
                ) : null}
              </div>
              <AdminPill tone={event.result === "success" ? "success" : "warning"}>
                {event.result === "success" ? "成功" : "失败"}
              </AdminPill>
            </AdminListRow>
          ))}
        </AdminList>
      ) : null}

      {!showOtherAppPlaceholder && subTab === "activity" && activityEvents.length > 0 ? (
        <AdminList>
          {activityEvents.map((event) => (
            <AdminListRow key={event.id} className="items-start">
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-medium text-foreground">{event.summary}</p>
                <p className="text-xs text-muted-foreground tabular-nums">{formatEventTime(event.created_at)}</p>
              </div>
            </AdminListRow>
          ))}
        </AdminList>
      ) : null}

      {!showOtherAppPlaceholder ? (
        <LogsPagination meta={meta} page={page} loading={loading} onPageChange={setPage} />
      ) : null}
    </div>
  );
}
